import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button, Alert } from "react-bootstrap";
import { Trash } from "react-bootstrap-icons";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
} from "../../../../context/ui-feedback/action";
import { useSearchTemplates } from "../../../../api/templates-api";
import { Loading } from "../../../common/loading/loading";
import { TemplateSelect } from "./template-select";

export const TemplateMultiSelect = ({
  selectedTemplates,
  formValidated,
  templatesValidationStatus,
  disabled,
  handleTemplateSelect,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const searchTemplates = useSearchTemplates();

  const { uiFeedbackDispatch } = useUiFeedback();

  const loadedTemplates = useRef(null);
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    const loadTemplate = async () => {
      createWaiting(uiFeedbackDispatch);
      try {
        const templateSearch = {
          deleted: false,
          draft: false,
          name: null,
          offset: 0,
          limit: 1000,
        };
        await searchTemplates(templateSearch, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            let templateData = data.templates.map((t) => {
              return {
                id: t.version_id,
                name: intl.formatMessage(
                  { id: "data.serviceworksheet.form.templatenameandversion" },
                  { name: t.name, version: t.version },
                ),
                template: t,
              };
            });

            setTemplates(templateData);
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.newserviceworksheet.exception.loadtemplate.title",
            }),
            intl.formatMessage({
              id: "page.newserviceworksheet.exception.loadtemplate.title",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      loadedTemplates.current === null &&
      templates === null &&
      checkNotEmpy([searchTemplates, intl, navigate, uiFeedbackDispatch])
    ) {
      loadTemplate();
      loadedTemplates.current = new Date();
    }
  }, [
    loadedTemplates,
    templates,
    searchTemplates,
    intl,
    navigate,
    uiFeedbackDispatch,
  ]);

  const onTemplateSelect = (index, template, name) => {
    if (index === -1) {
      handleTemplateSelect([
        ...selectedTemplates,
        { name: name, template: template },
      ]);
    } else {
      const newSelection = JSON.parse(JSON.stringify(selectedTemplates));
      newSelection[index] = {
        name: name,
        template: template,
      };
      handleTemplateSelect(newSelection);
    }
  };

  const onDeleteTemplate = (index) => {
    const newSelection = JSON.parse(JSON.stringify(selectedTemplates));
    newSelection.splice(index, 1);
    handleTemplateSelect(newSelection);
  };

  if (!templates) {
    return <Loading />;
  }

  return (
    <>
      {!disabled ? (
        <div className={selectedTemplates.length > 0 ? "mb-4" : null}>
          <TemplateSelect
            availableTemplates={templates}
            selectedTemplate={null}
            isValid={false}
            isInvalid={false}
            disabled={disabled}
            showName={false}
            handleTemplateSelect={(template, name) => {
              onTemplateSelect(-1, template, name);
            }}
          />
        </div>
      ) : null}
      {selectedTemplates.map((template, index) => (
        <div key={`${index}_${template.version_id}`} className="d-flex mb-2">
          <div className="w-100">
            <TemplateSelect
              availableTemplates={templates}
              selectedTemplate={template}
              isValid={
                formValidated &&
                (templatesValidationStatus[index]
                  ? templatesValidationStatus[index]
                  : false)
              }
              isInvalid={
                formValidated &&
                (!templatesValidationStatus[index]
                  ? !templatesValidationStatus[index]
                  : false)
              }
              disabled={disabled}
              showName={selectedTemplates.length > 1}
              handleTemplateSelect={(template, name) => {
                onTemplateSelect(index, template, name);
              }}
            />
          </div>
          <Button
            className="ms-2"
            variant="outline-danger"
            disabled={disabled}
            onClick={() => {
              onDeleteTemplate(index);
            }}
          >
            <Trash />
          </Button>
        </div>
      ))}
      {formValidated && selectedTemplates.length === 0 ? (
        <Alert className="mt-4" key="danger" variant="danger">
          <FormattedMessage id="page.editserviceworksheet.warning.templateisrequired" />
        </Alert>
      ) : null}
    </>
  );
};

TemplateMultiSelect.propTypes = {
  selectedTemplates: PropTypes.array.isRequired,
  formValidated: PropTypes.bool.isRequired,
  templatesValidationStatus: PropTypes.array,
  disabled: PropTypes.bool.isRequired,
  handleTemplateSelect: PropTypes.func.isRequired,
};
